/**
 * Function cleans up lines from "Error.stack" that are NOT from our code.
 * Remaining lines should be from our code and the last line should be the caller.
 *
 * This means calls made by:
 * - First line of the stack contains name of the error and messsage.
 * - Node.js internal code.
 * - 3rd party library.
 * - new Promise() calls which are only wrappers.
 *
 * are filtered out.
 */
export const getCallerMetadata = (stack = new Error().stack): string => {
  // const stack = new Error().stack;
  // This regex is matching:
  // - Usually the first line in Error --> "^Error"
  // - When call is made inside third party library --> "node_modules"
  // - When call is made inside node.js code --> "node:internal"
  // - new Promise (<anonymous>) may be the result of async function call for example in tests.
  // Final result should be a list of calls made by our own code.
  // and the last element should be initial call.
  const rowMatchingRegex = /(^Error|node_modules|node:internal|new Promise \(<anonymous>\))/;
  if (typeof stack === 'string') {
    const traceLines = stack.split('\n').filter((row) => !rowMatchingRegex.exec(row));
    if (Array.isArray(traceLines) && traceLines.length) {
      // This is safe to convert to String() because we check that
      // array has at least one element.
      return String(traceLines.pop()).replace(/^\s*at\s+/, '');
    }
  }
  return 'unknown';
};

/*
SPDX-SnippetBegin
SPDX-SnippetCopyrightText: Copyright 2021 Kent C. Dodds
SPDX-License-Identifier: NOASSERTION
SPDX-SnippetLicenseComment: https://kentcdodds.com/blog/get-a-catch-block-error-message-with-typescript
SPDX-SnippetComment: Copied simpliest original solution from https://kentcdodds.com/blog/get-a-catch-block-error-message-with-typescript on 2.4.2025.
*/
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message || error.name;
  return String(error);
};
/*
SPDX-SnippetEnd
*/

export const getErrorStack = (error: unknown): string => {
  if (error instanceof Error) return error.stack || getErrorMessage(error);
  return String(error);
};
